/** @format */
import { DestroyRef, Directive, ElementRef, inject, NgZone, OnInit } from '@angular/core';

/** Pointer movement (px) after which a press is treated as a drag instead of a click. */
const DRAG_THRESHOLD_PX = 5;

/** Drags started on these elements are ignored so that they keep their normal mouse behaviour. */
const INTERACTIVE_ELEMENT_SELECTOR = 'a, button, input, select, textarea, label, [role="button"], [contenteditable]';

const DRAGGING_CLASS = 'app-drag-to-scroll-dragging';
const OVER_TEXT_CLASS = 'app-drag-to-scroll-over-text';

type PointerEventType = 'pointerdown' | 'pointermove' | 'pointerup' | 'pointercancel' | 'pointerleave';

/**
 * Lets mouse users scroll a horizontally scrollable element by dragging it, so that they don't have to hit the scrollbar.
 * A drag can start anywhere except on text, which can still be selected, and on interactive elements, which keep their normal
 * mouse behaviour. Touch and pen input are left to the browser's native scrolling.
 *
 * The host gets the class `app-drag-to-scroll-over-text` while the mouse hovers text, and `app-drag-to-scroll-dragging` during a
 * drag, so that the cursor can be styled accordingly.
 */
@Directive({
    selector: '[appDragToScroll]',
    host: { class: 'app-drag-to-scroll' },
})
export class DragToScrollDirective implements OnInit {
    private readonly elementRef: ElementRef<HTMLElement> = inject(ElementRef);
    private readonly ngZone = inject(NgZone);
    private readonly destroyRef = inject(DestroyRef);

    private pointerId: number | null = null;
    private startX = 0;
    private startScrollLeft = 0;
    private dragging = false;

    ngOnInit(): void {
        const element = this.elementRef.nativeElement;
        const listeners: [PointerEventType, (event: PointerEvent) => void][] = [
            ['pointerdown', event => this.onPointerDown(event)],
            ['pointermove', event => this.onPointerMove(event)],
            ['pointerup', event => this.onPointerEnd(event)],
            ['pointercancel', event => this.onPointerEnd(event)],
            ['pointerleave', () => element.classList.remove(OVER_TEXT_CLASS)],
        ];
        // Pointer moves fire at a high rate and don't affect any bindings, so keep them from triggering change detection
        this.ngZone.runOutsideAngular(() => listeners.forEach(([type, listener]) => element.addEventListener(type, listener)));
        this.destroyRef.onDestroy(() => listeners.forEach(([type, listener]) => element.removeEventListener(type, listener)));
    }

    private onPointerDown(event: PointerEvent): void {
        const element = this.elementRef.nativeElement;
        if (
            event.pointerType !== 'mouse' ||
            event.button !== 0 ||
            element.scrollWidth <= element.clientWidth ||
            (event.target as Element | null)?.closest(INTERACTIVE_ELEMENT_SELECTOR) ||
            this.isOverText(event.clientX, event.clientY)
        ) {
            return;
        }
        this.pointerId = event.pointerId;
        this.startX = event.clientX;
        this.startScrollLeft = element.scrollLeft;
        this.dragging = false;
    }

    private onPointerMove(event: PointerEvent): void {
        const element = this.elementRef.nativeElement;
        if (event.pointerId !== this.pointerId) {
            if (event.pointerType === 'mouse' && this.pointerId === null) {
                element.classList.toggle(OVER_TEXT_CLASS, this.isOverText(event.clientX, event.clientY));
            }
            return;
        }
        // The press may have been released outside the host before the drag started and the pointer was captured.
        // An even value means that the primary button is not pressed.
        if (event.buttons % 2 === 0) {
            this.onPointerEnd(event);
            return;
        }
        const deltaX = event.clientX - this.startX;
        if (!this.dragging) {
            if (Math.abs(deltaX) < DRAG_THRESHOLD_PX) {
                return;
            }
            this.dragging = true;
            element.setPointerCapture(event.pointerId);
            element.classList.add(DRAGGING_CLASS);
            // Discard any text selection the press may have started
            window.getSelection()?.removeAllRanges();
        }
        element.scrollLeft = this.startScrollLeft - deltaX;
    }

    private onPointerEnd(event: PointerEvent): void {
        if (event.pointerId !== this.pointerId) {
            return;
        }
        const element = this.elementRef.nativeElement;
        if (element.hasPointerCapture(event.pointerId)) {
            element.releasePointerCapture(event.pointerId);
        }
        if (this.dragging) {
            this.suppressNextClick();
        }
        element.classList.remove(DRAGGING_CLASS);
        this.pointerId = null;
        this.dragging = false;
    }

    /** Keeps the click that the browser fires at the end of a drag from activating whatever is under the pointer. */
    private suppressNextClick(): void {
        const element = this.elementRef.nativeElement;
        const suppressClick = (event: MouseEvent) => {
            event.preventDefault();
            event.stopPropagation();
        };
        element.addEventListener('click', suppressClick, { capture: true, once: true });
        // The click, if any, is dispatched right after pointerup, so a later click belongs to a new press
        setTimeout(() => element.removeEventListener('click', suppressClick, { capture: true }));
    }

    /** Whether the given viewport coordinates are on top of rendered, non-blank text inside the host element. */
    private isOverText(x: number, y: number): boolean {
        const node = caretNodeFromPoint(x, y);
        if (node?.nodeType !== Node.TEXT_NODE || !node.textContent?.trim() || !this.elementRef.nativeElement.contains(node)) {
            return false;
        }
        // The caret position is the one closest to the point, so check that the point is actually within the text's line boxes
        const range = document.createRange();
        range.selectNodeContents(node);
        return Array.from(range.getClientRects()).some(rect => x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom);
    }
}

function caretNodeFromPoint(x: number, y: number): Node | null {
    if ('caretPositionFromPoint' in document) {
        return document.caretPositionFromPoint(x, y)?.offsetNode ?? null;
    }
    // Fallback for older browsers (Chrome and Edge before v128, Safari before v26.2)
    // eslint-disable-next-line deprecation/deprecation
    return (document as Document).caretRangeFromPoint?.(x, y)?.startContainer ?? null;
}
